import {memo, shapeComponent, ShapeComponent, Text, useBreakpoint, useCurrentUser, useI18n, View} from "shared/base"
import React, {useEffect, useMemo} from "react"
import {SchoolClass, Subscription, User} from "models"
import Button from "components/chalky-layout/button"
import ChalkyLayout from "components/chalky-layout"
import isUserA from "components/users/is-a"
import PageContentsPresentation from "components/page-contents/presentation"
import ProUpgradeOverlay from "components/pro/upgrade-overlay"
import Routes from "shared/routes"
import SchoolClassRow from "./dashboard/school-class-row"
import useAnythingPro from "components/subscriptions/use-anything-pro"
import useCollection from "@kaspernj/api-maker/build/use-collection"
import useCreatedEvent from "@kaspernj/api-maker/build/use-created-event"
import {useFrontLayout} from "components/front-layout"

export default memo(shapeComponent(class RoutesUserDashboardRoute extends ShapeComponent {
  setup() {
    const {t} = useI18n({namespace: "js.routes.user.my_classrooms_route"})
    const currentUser = useCurrentUser()
    const {anythingPro} = useAnythingPro({currentUser})

    const mySchoolClassesQuery = useMemo(() => {
      const userContactID = currentUser?.contactId() || "no-user-uuid"
      const isTeacher = isUserA(currentUser, "teacher")
      const isAdminOrSchoolLeader = isUserA(currentUser, ["admin", "school_leader"])

      if (isAdminOrSchoolLeader && !isTeacher) {
        return SchoolClass.ransack({with_contact_id: userContactID})
      }

      return SchoolClass.ransack({
        where_user_is_a: JSON.stringify({user_contact_id: userContactID, roles: ["teacher"]})
      })
    }, [currentUser?.id()])

    const currentUserOverlayQuery = useMemo(() => User.ransack({id_eq: currentUser?.id() || "no-user-uuid"}), [currentUser?.id()])

    this.useStates({
      lastUpdate: () => new Date(),
      seenDashboardUpgradeOverlayThisSession: false,
      showUpgradeOverlay: false
    })

    const {models: schoolClasses} = useCollection({
      abilities: {
        SchoolClass: ["softDelete", "edit"]
      },
      collection: mySchoolClassesQuery,
      groupBy: ["id"],
      modelClass: SchoolClass,
      preloads: ["active_subscriptions.plan", "class_step", "school.account", "school.country", "teachers"],
      select: {
        Account: ["id"],
        ClassStep: ["primarySchoolStep"],
        Country: ["countryCode"],
        Plan: ["identifier", "name"],
        School: ["id", "name"],
        SchoolClass: ["contactId", "id", "interpretedLegalName", "interpretedName"],
        Subscription: ["id"],
        User: ["id", "name"]
      }
    }, [currentUser?.id(), this.s.lastUpdate])

    const {models: currentUsersWithOverlayFlag} = useCollection({
      collection: currentUserOverlayQuery,
      groupBy: ["id"],
      modelClass: User,
      select: {
        User: ["id", "seenDashboardFirstVisitUpgradeOverlay"]
      }
    }, [currentUser?.id()])
    const currentUserWithOverlayFlag = currentUsersWithOverlayFlag?.[0]
    const overlayFlagUser = currentUserWithOverlayFlag || currentUser

    this.setInstance(useBreakpoint())
    useCreatedEvent(Subscription, this.tt.onSubscriptionCreated)

    this.setInstance({
      anythingPro,
      currentUser,
      overlayFlagUser,
      schoolClasses,
      t
    })

    useFrontLayout()?.setState({active: "user-dashboard", headerTitle: t(".dashboard")})

    useEffect(() => {
      if (
        !currentUser?.id() ||
        anythingPro !== false ||
        schoolClasses == null ||
        schoolClasses.length > 0 ||
        this.s.seenDashboardUpgradeOverlayThisSession ||
        this.hasSeenDashboardUpgradeOverlayInDatabase() ||
        this.s.showUpgradeOverlay
      ) {
        return
      }

      this.setState({
        seenDashboardUpgradeOverlayThisSession: true,
        showUpgradeOverlay: true
      })
    }, [anythingPro, currentUser?.id(), overlayFlagUser?.id(), schoolClasses?.length])
  }

  render() {
    const {mdUp, schoolClasses, t} = this.tt

    return (
      <ChalkyLayout active="dashboard" centered={false} dataSet={this.chalkyLayoutDataSet ||= {route: "user/dashboard"}}>
        {this.s.showUpgradeOverlay &&
          <ProUpgradeOverlay
            mainContentIdentifier="pro/upgrade-overlay/dashboard-first-visit-main-content"
            onRequestClose={this.tt.onUpgradeOverlayRequestClose}
          />
        }
        <View
          style={this.cache("containerStyle", {
            flexDirection: mdUp ? "row" : "column"
          }, [mdUp])}
        >
          <View
            style={this.cache("myClassesInfoStyle", {
              width: mdUp ? 320 : "100%"
            }, [mdUp])}
          >
            <Text
              style={this.cache("myClassesTextStyle", {
                color: "#2c2c96",
                fontFamily: "DreamChaser",
                fontSize: 48,
                textAlign: mdUp ? undefined : "center"
              }, [mdUp])}
            >
              {t(".my_classes")}
            </Text>
            <Text
              style={this.cache("myClassesDescriptionTextStyle", {
                flexWrap: "wrap",
                marginBottom: 24,
                color: "#364665",
                textAlign: mdUp ? undefined : "center"
              }, [mdUp])}
            >
              {t(".my_classes_description")}
            </Text>
            <Button
              dataSet={this.createANewClassButtonDataSet ||= {class: "create-a-new-class-button"}}
              icon="plus"
              primary
              title={t(".create_a_new_class")}
              to={Routes.userCreateClassStep1Path()}
            />
          </View>
          <View
            style={this.cache("classesColumnStyle", {
              flex: 1,
              marginTop: mdUp ? 12 : 48,
              marginLeft: mdUp ? 48 : undefined
            }, [mdUp])}
          >
            {schoolClasses?.map((schoolClass, schoolClassIndex) =>
              <SchoolClassRow
                key={schoolClass.id()}
                lastRow={schoolClassIndex + 1 >= schoolClasses.length}
                schoolClass={schoolClass}
              />
            )}
            {schoolClasses?.length === 0 &&
              <PageContentsPresentation
                defaultContent={
                  <>
                    <Text>{t(".no_school_classes_found_line_1")}</Text>
                    <Text>{t(".no_school_classes_found_line_2")}</Text>
                  </>
                }
                identifier="user/dashboard/no-school-classes-found"
              />
            }
          </View>
        </View>
      </ChalkyLayout>
    )
  }

  // Reloads school classes when a matching subscription is created, so that the school class is moved from limit-access to Pro container
  onSubscriptionCreated = ({model: subscription}) => {
    const schoolClassWithSubscription = this.schoolClasses?.find((schoolClass) =>
      schoolClass.contactId() == subscription.contactId() ||
        schoolClass.school()?.contactId() == subscription.contactId()
    )

    if (schoolClassWithSubscription || this.currentUser?.contactId() == subscription.contactId()) {
      this.setState({lastUpdate: new Date()})
    }
  }

  onUpgradeOverlayRequestClose = async () => {
    await this.markDashboardUpgradeOverlaySeenInDatabase()
    this.setState({showUpgradeOverlay: false})
  }

  hasSeenDashboardUpgradeOverlayInDatabase = () => {
    return Boolean(this.overlayFlagUser.seenDashboardFirstVisitUpgradeOverlay())
  }

  markDashboardUpgradeOverlaySeenInDatabase = async () => {
    await this.overlayFlagUser.update({seenDashboardFirstVisitUpgradeOverlay: true})
  }
}))
